import React, { useMemo } from 'react';

import { GetStaticPaths } from 'next';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import SelectedBlog from '../../../../components/blog/SelectedBlog';
import {
  BlogContextProvider,
  BlogFilterContextProvider,
} from '../../../../components/blog/helpers';
import LanguageMessage from '../../../../components/common/LanguageMessage';
import Layout from '../../../../components/common/Layout';
import Strip from '../../../../components/common/Strip';
import { MobileContextProvider, createBlogFilterKey, getAsset } from '../../../../helpers';
import { useBlogsStaticPaths, useItems } from '../../../../hooks';
import { SINGLE_BLOG } from '../../../../queries';
import { withTranslation } from '../../../../translation';

const BlogPage = ({ pageData }: any) => {
  const router = useRouter();

  const { t, i18n } = useTranslation('resources');
  const { language } = i18n;

  const { blog, relatedPosts, selectedPost, tags, top_posts } = pageData;

  const { articles, blogPath } = t('blog', {
    returnObjects: true,
  }) as any;

  const blogTagsList = blog?.reduce((acc: any[], curr: { tags: any[] }) => {
    curr.tags.map((tag) => {
      acc.push(tag.tags_id.id);
    });

    return acc;
  }, []);

  const languageMessage: { title: string; description: string } = t('languageMessage', {
    returnObjects: true,
  }) as any;

  const content = selectedPost[0].translations[0];
  const postTags = selectedPost[0].tags
    .map(
      (tag: { tags_id: { translations: { name: any }[] } }) => tag.tags_id?.translations[0]?.name
    )
    .filter(Boolean);

  const tagFilters = useMemo(
    () =>
      tags.map(({ id, translations }: { id: string; translations: { name: string }[] }) => ({
        key: createBlogFilterKey('tags', id),
        name: translations[0]?.name,
        condition: id,
        id,
      })),
    [tags]
  );

  return (
    <MobileContextProvider>
      <Layout
        SEO={{
          title: content?.meta_title || content?.title || '',
          meta: {
            description: content?.seo || content?.summary || '',
          },
          image: getAsset(selectedPost[0].thumbnail).path,
        }}
      >
        <BlogContextProvider topPosts={top_posts} blogTagsList={blogTagsList}>
          <BlogFilterContextProvider blogs={[]} tags={tagFilters} authors={[]}>
            {!content ? (
              <Strip variant="white">
                <LanguageMessage data={languageMessage} />
              </Strip>
            ) : (
              <>
                <Strip variant="white">
                  <div
                    onClick={() => router.back()}
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {`${'< Back'}`}
                  </div>

                  <SelectedBlog
                    data={{
                      category: content.category,
                      title: content.title,
                      body: content.body,
                      biography: {
                        body: content.biography,
                        image: getAsset(content.biography_img),
                      },
                      author: selectedPost[0].authors[0]?.authors_id.name,
                      authorAt: selectedPost[0].authors[0]?.authors_id.author_at,
                      authorTexts: articles.authorTexts,
                      authorImg: getAsset(selectedPost[0].authors[0]?.authors_id.author_img),
                      img: getAsset(selectedPost[0].thumbnail, true),
                      date: new Date(selectedPost[0].publish_date).toLocaleDateString(language, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }),
                      updated: new Date(selectedPost[0].date_updated).toLocaleDateString(language, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }),
                      tags: postTags,
                      slug: `${blogPath}${content.slug}/`,
                    }}
                    relatedPosts={relatedPosts}
                  />
                </Strip>
              </>
            )}
          </BlogFilterContextProvider>
        </BlogContextProvider>
      </Layout>
    </MobileContextProvider>
  );
};

export default BlogPage;

export const getStaticPaths: GetStaticPaths = async () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const slugs = await useBlogsStaticPaths();

  const paths = slugs.map((slug: any) => ({
    params: { slug },
  }));

  return {
    paths,
    fallback: false, // Change to 'blocking' if you want to support dynamic paths
  };
};

export const getStaticProps = withTranslation(async (context) => {
  const slug = context.params?.slug as string;

  if (!slug) {
    return { notFound: true };
  }

  // ✅ Fetch the blog post first to get its tags
  const blogData = await useItems(SINGLE_BLOG, { slug });

  if (!blogData || !blogData.items?.selectedPost) {
    return { notFound: true };
  }

  // ✅ Extract the tags from the post
  const tags =
    blogData.items.selectedPost[0].tags?.map(
      (tag: { tags_id: { name: any } }) => tag.tags_id.name
    ) || [];

  // ✅ Fetch the blog again, now with tags included
  const fullBlogData = await useItems(SINGLE_BLOG, { slug, tags });

  return {
    props: {
      pageData: fullBlogData.items,
    },
    revalidate: 10,
  };
});
